import {
  APEX_CLASS,
  APEX_TRIGGER,
  APPROVAL_PROCESS,
  FLOW,
  LAYOUT,
  VALIDATION_RULE,
  WORKFLOW_RULE,
} from './parser';

export enum ChatCompletionRequestMessageRole {
  SYSTEM = 'system',
  USER = 'user',
  ASSISTANT = 'assistant',
}
export type SalesforceConfigurationType = string;

export type AiChatTextContentPart = {
  type: 'text';
  text: string;
};

export type AiChatArtifactContentPart = {
  type: 'artifact';
  artifactId: string;
  versionId: string;
  title: string;
};

export type AiChatTableContentPart = {
  type: 'table';
  tableId: string;
};

export enum BuildDefinitionStatus {
  Draft = 'draft',
  Building = 'building',
  Success = 'success',
  PartialSuccess = 'partial_success',
  Failed = 'failed',
}

export interface BuildDefinitionProperties {
  id: string;
  chatId: string;
  status: BuildDefinitionStatus;
  text?: string;
}

export type AiChatBuildDefinitionContentPart = {
  type: 'build_definition';
  buildDefinitionId: string;
};

export type AiChatQuestion = {
  text: string;
  multipleChoice: boolean;
  options: string[];
};

export type AiChatQuestionsContentPart = {
  type: 'questions';
  questions: AiChatQuestion[];
};

export type AiChatAnswer = {
  question: string;
  answer?: string;
  skipped: boolean;
};

export type AiChatAnswersContentPart = {
  type: 'answers';
  answers: AiChatAnswer[];
};

export enum ChatAttachmentAllowedExtension {
  txt = 'txt',
  json = 'json',
  xml = 'xml',
  csv = 'csv',
  tsv = 'tsv',
  log = 'log',
  ini = 'ini',
  env = 'env',
  md = 'md',
  rst = 'rst',
  tex = 'tex',
  toml = 'toml',
  nacl = 'nacl',
  config = 'config',
  plist = 'plist',
  pdf = 'pdf',
  docx = 'docx',
  png = 'png',
  jpg = 'jpg',
  jpeg = 'jpeg',
  webp = 'webp',
}

// Fallback MIME used by browsers that can't resolve a type. Callers should accept this for
// any known extension in addition to the values listed in CHAT_ATTACHMENT_EXTENSION_TO_MIME.
export const CHAT_ATTACHMENT_FALLBACK_MIME = 'application/octet-stream';

// MIME hints used to cross-check the client-supplied extension.
export const CHAT_ATTACHMENT_EXTENSION_TO_MIME: Record<
  ChatAttachmentAllowedExtension,
  readonly string[]
> = {
  [ChatAttachmentAllowedExtension.txt]: ['text/plain'],
  [ChatAttachmentAllowedExtension.json]: ['application/json', 'text/json'],
  [ChatAttachmentAllowedExtension.xml]: ['application/xml', 'text/xml'],
  [ChatAttachmentAllowedExtension.csv]: ['text/csv', 'application/csv'],
  [ChatAttachmentAllowedExtension.tsv]: ['text/tab-separated-values', 'text/tsv'],
  [ChatAttachmentAllowedExtension.log]: ['text/plain', 'text/x-log'],
  [ChatAttachmentAllowedExtension.ini]: ['text/plain'],
  [ChatAttachmentAllowedExtension.env]: ['text/plain'],
  [ChatAttachmentAllowedExtension.md]: ['text/markdown', 'text/plain'],
  [ChatAttachmentAllowedExtension.rst]: ['text/x-rst', 'text/plain'],
  [ChatAttachmentAllowedExtension.tex]: ['text/x-tex', 'application/x-tex', 'text/plain'],
  [ChatAttachmentAllowedExtension.toml]: ['application/toml', 'text/plain'],
  [ChatAttachmentAllowedExtension.nacl]: ['text/plain'],
  [ChatAttachmentAllowedExtension.config]: ['text/plain'],
  [ChatAttachmentAllowedExtension.plist]: ['application/x-plist', 'text/plain', 'application/xml'],
  [ChatAttachmentAllowedExtension.pdf]: ['application/pdf'],
  [ChatAttachmentAllowedExtension.docx]: [
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
  [ChatAttachmentAllowedExtension.png]: ['image/png'],
  [ChatAttachmentAllowedExtension.jpg]: ['image/jpeg'],
  [ChatAttachmentAllowedExtension.jpeg]: ['image/jpeg'],
  [ChatAttachmentAllowedExtension.webp]: ['image/webp'],
};

export type AiChatAttachmentContentPart = {
  type: 'attachment';
  /** Server-assigned on persist; present on all persisted parts. */
  attachmentId?: string;
  fileType: ChatAttachmentAllowedExtension | 'clipboard';
  name: string;
  /**
   * Transient: populated on write (client → server) and re-injected by hydrateForPython
   * for Python context. Stripped from the persisted message by assignAttachmentIds
   * (never stored in aiChatDetails; lives in AiChatAttachment.parsedText).
   */
  parsedText?: string;
  /**
   * Client-generated correlator for binary uploads. Sent with the message and echoed back in
   * the response (and in subsequent reads) so the client can map files to `attachmentId`.
   * Becomes dead data after the upload completes.
   */
  _id?: string;
};

export interface ParsedAttachment {
  name: string;
  fileType: ChatAttachmentAllowedExtension;
  parsedText: string;
}

export interface fileDownloadUrlResponse {
  downloadUrl: string;
}

export const CHAT_ATTACHMENT_MAX_CHARS = 200_000;

// Total parsed-character cap for all attachments combined within a single user message.
// The server enforces this per upload-batch; the client enforces it per message across
// every attachment (clipboard + files) that will be sent in a single send.
export const MAX_CHARS_PER_BATCH = 200_000;

// Counts user-perceived characters (grapheme clusters per Unicode UAX #29) so the cap behaves
// fairly across scripts with combining marks (Hebrew niqqud, Devanagari, Thai), decomposed
// Latin, and modern emoji (ZWJ sequences, skin tones, regional indicator pairs).
const GRAPHEME_SEGMENTER = new Intl.Segmenter(undefined, { granularity: 'grapheme' });

export const countGraphemes = (text: string): number => {
  const iterator = GRAPHEME_SEGMENTER.segment(text)[Symbol.iterator]();
  let count = 0;
  while (!iterator.next().done) count++;
  return count;
};

export type AiChatMessageContentPart =
  | AiChatTextContentPart
  | AiChatArtifactContentPart
  | AiChatTableContentPart
  | AiChatBuildDefinitionContentPart
  | AiChatQuestionsContentPart
  | AiChatAnswersContentPart
  | AiChatAttachmentContentPart;

// Support both simple strings (backward compatible) and structured content
export type AiChatMessageContent = string | AiChatMessageContentPart[];
export type AiChatMessageReplyTo = string;

export interface ConfigurationDescription {
  data: string;
  error: string;
}

export interface ChatGptPromptMessage {
  role: ChatCompletionRequestMessageRole;
  content: AiChatMessageContent;
  replyTo?: AiChatMessageReplyTo;
}

export enum AiChatContextType {
  AUTOMATION = 'automation',
  CONFIGURATION = 'configuration',
  FIELD = 'field',
}

export interface AiChatConfigurationContext {
  type: AiChatContextType.CONFIGURATION;
  metadataId: string;
}

export interface AiChatFieldContext {
  type: AiChatContextType.FIELD;
  fieldName: string;
  objectName: string;
}

export interface AiChatAutomationContext {
  type: AiChatContextType.AUTOMATION;
  // TODO this should be AiChatAutomationContextDto, which is using AutomationDetailsDto,
  //  but we can't use it as it's breaking the client, need to create an interface to use instead of the dto class
  automationProperties: any;
}

export type AiChatContextProperties =
  | AiChatConfigurationContext
  | AiChatFieldContext
  | AiChatAutomationContext;

export type AiChatFolderMetadata = {
  id: string;
  name: string;
  createdAt: Date;
};

export type PlaygroundSupportContext = {
  sfdcUserId?: string;
  recordId?: string;
  objectName?: string;
};

export type SupportContext = PlaygroundSupportContext & {
  fullName?: string;
  pageUrl?: string;
};

export type AiChatMetadata = {
  id: string;
  name: string;
  isFavorite: boolean;
  folderId?: string | null;
  agentId: string;
  agentType: AgentType;
  updatedAt: Date;
  supportContext?: SupportContext;
};

export enum AiChatMessageMode {
  None = 'none',
  Research = 'researchMode',
  Build = 'buildMode',
  MultiOrg = 'multiOrgMode',
  Artifact = 'artifactMode',
}

export interface AiChatUserMessage extends TimedChatGptPromptMessage {
  role: ChatCompletionRequestMessageRole.USER;
  userId?: string;
  mode?: AiChatMessageMode;
  modeContext?: {
    additionalOrgNames?: string[];
  };
}

export interface AiChatAssistantMessage extends TimedChatGptPromptMessage {
  role: ChatCompletionRequestMessageRole.ASSISTANT;
  isError: boolean;
  runId?: string; // LangSmith run ID for feedback tracking
}

export type AiChatMessage = AiChatUserMessage | AiChatAssistantMessage;

export interface AiChatDetails {
  messages: AiChatMessage[];
}

export interface TimedChatGptPromptMessage extends ChatGptPromptMessage {
  createdAt: Date;
}

type MarkdownExportMessage = Pick<ChatGptPromptMessage, 'role' | 'content'> & {
  hidden?: boolean;
};

const extractTextContentForMarkdown = (content: AiChatMessageContent): string => {
  if (typeof content === 'string') return content;

  return content
    .filter((part): part is AiChatTextContentPart => part.type === 'text')
    .map((part) => part.text)
    .join('');
};

export const serializeVisibleTextChatToMarkdown = (
  chatName: string,
  messages: MarkdownExportMessage[],
): string => {
  const lines: string[] = [`# ${chatName}`, ''];

  for (const message of messages) {
    if (message.hidden) continue;

    const text = extractTextContentForMarkdown(message.content);
    if (!text.trim()) continue;

    const role =
      message.role === ChatCompletionRequestMessageRole.USER ? '**You**' : '**Assistant**';

    lines.push(role, '', text, '');
  }

  return lines.join('\n');
};

export type NewChatResponse = {
  id: string;
  name: string;
  supportContext?: SupportContext;
};

export type PromptInputVariables = {
  message: string;
  secondary_message?: string;
};

export enum AgentType {
  Documentation = 'Documentation',
  ProcessOptimization = 'Process Optimization',
  Monitoring = 'Monitoring',
  MonitoringV2 = 'MonitoringV2',
  Permissions = 'Permissions',
  Support = 'Support',
}

export enum ChatSource {
  Sweep = 'Sweep',
  Salesforce = 'Salesforce',
}

export enum AgentFileAllowedExtension {
  txt = 'txt',
  json = 'json',
  xml = 'xml',
  csv = 'csv',
  tsv = 'tsv',
  log = 'log',
  ini = 'ini',
  env = 'env',
  md = 'md',
  rst = 'rst',
  tex = 'tex',
  toml = 'toml',
  nacl = 'nacl',
  config = 'config',
  plist = 'plist',
  pdf = 'pdf',
}

export enum AgentFileStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  FAILED = 'failed',
}

export interface AgentFileProperties {
  id: string;
  name: string;
  type: AgentFileAllowedExtension;
  status: AgentFileStatus;
  createdById: string;
  createdAt: Date;
}

export interface ProcessOptimizationContextComponent {
  funnelId: string;
  stagesIds: string[];
}

export interface MonitoringContextComponent {
  objectType: string; // TODO change to objectName (data migration)
}

export interface MonitoringV2ContextComponent {
  objectType?: string; // TODO change to objectName (data migration)
  metadataType?: SalesforceConfigurationType; // Single metadata type, mutually exclusive with objectType
}

export interface MetadataElementReference {
  name: string;
  type: string;
}

export interface AiAgentBestPracticeAttachment {
  bestPracticeId: string;
  createdAt: Date;
}

interface AiAgentPropertiesBase {
  id: string;
  name: string;
  type: AgentType;
  userInstructions?: string;
  systemInstructions?: string;
  files: AgentFileProperties[];
  bestPractices: AiAgentBestPracticeAttachment[]; // TBD - only in MonitoringV2 or in the base?
  orgId: string;
  projectId?: string | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiAgentPropertiesProcess extends AiAgentPropertiesBase {
  type: AgentType.ProcessOptimization;
  contextComponents: ProcessOptimizationContextComponent[];
}

export interface AiAgentPropertiesDocumentation extends AiAgentPropertiesBase {
  type: AgentType.Documentation;
  contextComponents: never;
}

export interface AiAgentPropertiesMonitoring extends AiAgentPropertiesBase {
  type: AgentType.Monitoring;
  contextComponents: MonitoringContextComponent;
}

export interface AiAgentPropertiesMonitoringV2 extends AiAgentPropertiesBase {
  type: AgentType.MonitoringV2;
  contextComponents: MonitoringV2ContextComponent;
  metadataElements?: MetadataElementReference[];
}

export interface AiAgentPropertiesPermissions extends AiAgentPropertiesBase {
  type: AgentType.Permissions;
  contextComponents: never;
}

export interface AiAgentPropertiesSupport extends AiAgentPropertiesBase {
  type: AgentType.Support;
  contextComponents: never;
}

export type AgentContextComponent = AiAgentProperties['contextComponents'];

export type AiAgentProperties =
  | AiAgentPropertiesDocumentation
  | AiAgentPropertiesProcess
  | AiAgentPropertiesMonitoring
  | AiAgentPropertiesMonitoringV2
  | AiAgentPropertiesPermissions
  | AiAgentPropertiesSupport;

export interface AgentFact {
  key: string;
  value: string | string[] | number;
}

export enum AgentFactKey {
  TOTAL_CHATS = 'totalChats',
  TOTAL_USER_CHATS = 'totalUserChats',
  FUNNEL_NAMES = 'funnelNames',
  TOTAL_ISSUES = 'totalIssues',
  OBJECT_TYPE = 'objectType',
}

export interface TotalChatsFact extends AgentFact {
  key: AgentFactKey.TOTAL_CHATS;
  value: number;
}

export interface TotalUserChatsFact extends AgentFact {
  key: AgentFactKey.TOTAL_USER_CHATS;
  value: number;
}

export interface FunnelNamesFact extends AgentFact {
  key: AgentFactKey.FUNNEL_NAMES;
  value: string[];
}

export interface TotalIssuesFact extends AgentFact {
  key: AgentFactKey.TOTAL_ISSUES;
  value: number;
}

export interface ObjectTypeFact extends AgentFact {
  key: AgentFactKey.OBJECT_TYPE;
  value: string;
}

export type DocumentationAgentFacts = (TotalChatsFact | TotalUserChatsFact)[];
export type ProcessOptimizationAgentFacts = FunnelNamesFact[];
export type MonitoringV2AgentFacts = (TotalIssuesFact | ObjectTypeFact)[];
export type PermissionsAgentFacts = (TotalChatsFact | TotalUserChatsFact)[];
export type SupportAgentFacts = TotalChatsFact[];

export type AiAgentPropertiesWithFacts =
  | (AiAgentPropertiesDocumentation & { facts: DocumentationAgentFacts })
  | (AiAgentPropertiesProcess & { facts: ProcessOptimizationAgentFacts })
  | (AiAgentPropertiesMonitoring & { facts: [] })
  | (AiAgentPropertiesMonitoringV2 & { facts: MonitoringV2AgentFacts })
  | (AiAgentPropertiesPermissions & { facts: PermissionsAgentFacts })
  | (AiAgentPropertiesSupport & { facts: SupportAgentFacts });

/**
 * This map is used to get the type of the AiAgentProperties based on the type (AgentType) of the agent.
 */
export type AiAgentPropertiesDiscriminatedUnionMap = {
  [K in AiAgentProperties['type']]: Extract<AiAgentProperties, { type: K }>;
};

/**
 * Use this type guard to check and cast to the concrete type of the agent.
 */
export function isAiAgentProperties<T extends AgentType>(
  feature: AiAgentPropertiesBase,
  type: T,
): feature is AiAgentPropertiesDiscriminatedUnionMap[T];

/**
 * Use this type guard to check that feature is of any valid AgentType.
 */
export function isAiAgentProperties(feature: AiAgentPropertiesBase): feature is AiAgentProperties;

export function isAiAgentProperties<T extends AgentType | undefined>(
  feature: AiAgentPropertiesBase,
  type?: T,
): feature is T extends AgentType ? AiAgentPropertiesDiscriminatedUnionMap[T] : AiAgentProperties {
  if (type === undefined) {
    return Object.values(AgentType).includes(feature.type);
  } else {
    return feature.type === type;
  }
}

export interface AiArtifactMetadata {
  id: string; // Version-specific UUID (from Python's version_id)
  artifactId: string; // Stable artifact identifier (from Python's artifact_id)
  chatId: string;
  userId: string;
  title: string;
  version: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface AiSessionInstructionProperties {
  id: string;
  name: string;
  content: string;
  crmOrgId: string;
}

export interface AiSessionInstructionUpdateableProperties {
  name: string;
  content: string;
}

//renaming type to match client's implementation
export type AiSupportInstructionProperties = AiSessionInstructionProperties;
export type AiSupportInstructionUpdateableProperties = AiSessionInstructionUpdateableProperties;

export const AI_MONITORING_AGENT_OBJECT_CONTEXT_METADATA_TYPES = [
  APEX_CLASS,
  APEX_TRIGGER,
  FLOW,
  LAYOUT,
  APPROVAL_PROCESS,
  WORKFLOW_RULE,
  VALIDATION_RULE,
];
